import _ from 'lodash';

// Based on api/export.sample.csv from the design handoff: 5 "#" provenance
// header lines, then 9 fixed columns + 3 per-risk columns from
// riskPresentation.js#csvColumns. Note the CSV formatting quirks that
// diverge from on-screen copy: the unit is the bare letter ("28 F", not
// "28 °F") and the climatology range uses a plain hyphen ("1981-2025", not
// the en dash shown on screen).
//
// The xclim engine/version is deliberately absent from the header — it was
// dropped from the on-screen footer too, and an export that names it invites
// the reader to treat it as part of the result.
function csvEscape(value) {
    const str = String(value == null ? '' : value);
    return `"${str.replace(/"/g, '""')}"`;
}

function csvField(value) {
    if (value == null) return '';
    const str = String(value);
    return /[",\n]/.test(str) ? csvEscape(str) : str;
}

export function buildRiskCsv(data, presentation) {
    if (!data || !presentation) return '';
    const { location, provenance, period, threshold, units, aggregation, buckets } = data;
    const cw = provenance.climatology_window || {};

    // The pin, as one comma-free string. The obvious "(lat, lon)" spelling puts a comma
    // in a header line, and a spreadsheet splits the line there into two cells — the
    // "#" prefix means nothing to it, these are ordinary rows.
    const place = [location.label, `lat ${location.lat}`, `lon ${location.lon}`, location.grid_cell]
        .filter((part) => part != null && part !== '')
        .join(' | ');

    // Each header line is emitted as a single quoted field so it lands in one cell
    // whatever punctuation it carries.
    const header = [
        `# ${presentation.csvTitle}`,
        `# location: ${place}`,
        `# threshold: ${threshold} ${units} | aggregation: ${aggregation} | period: ${period.start_date}..${period.end_date}`,
        `# source: ${provenance.source}${provenance.grid ? ' / ' + provenance.grid : ''} | climatology: ${cw.start_year}-${cw.end_year} (${cw.n_years} years)`,
        `# computed_at: ${provenance.computed_at} | note: ${(provenance.notes || []).join('; ')}`,
    ].map(csvEscape);

    const columns = [
        { header: 'period_start', get: (b) => b.start_date },
        { header: 'period_end', get: (b) => b.end_date },
        { header: 'label', get: (b) => b.label, alwaysQuote: true },
        { header: 'days', get: (b) => b.days },
        { header: 'is_partial', get: (b) => b.is_partial },
        // Unrounded — the display value (probability_pct) is what's rounded.
        { header: 'probability', get: (b) => b.probability },
        { header: 'probability_pct', get: (b) => b.pctDisplay },
        { header: 'years_with_event', get: (b) => b.years_with_event },
        { header: 'years_total', get: (b) => b.years_total },
        // chill_hours (period-only, no weekly detail) has no csvColumns — Risk.html
        // never renders a Download CSV button for it, but this stays defensive rather
        // than throwing if the function is ever called for it directly.
        ...(presentation.csvColumns || []).map((c) => ({ header: c.header, get: (b) => _.get(b, c.path) })),
    ];

    const lines = [columns.map((c) => c.header).join(',')];
    buckets.forEach((bucket) => {
        lines.push(
            columns
                .map((c) => {
                    const value = c.get(bucket);
                    return c.alwaysQuote ? csvEscape(value) : csvField(value);
                })
                .join(',')
        );
    });

    return [...header, ...lines].join('\n');
}

export function riskCsvFilename(data) {
    if (!data) return 'risk-probability.csv';
    const { indicator, location, period } = data;
    return `${indicator}-risk-weekly_${location.lat}_${location.lon}_${period.start_date}_${period.end_date}.csv`;
}

// The BOM is what makes Excel read the file as UTF-8. Without it Excel falls back to
// the system legacy encoding, and the en dash in every bucket label ("Mar 1 – Mar 7",
// UTF-8 e2 80 93) arrives as mojibake. CRLF for the same audience: it is what Excel
// expects to see in a .csv.
export function toSpreadsheetCsv(csvText) {
    return '\uFEFF' + csvText.replace(/\r?\n/g, '\r\n');
}

export function downloadCsvText(csvText, filename) {
    const blob = new Blob([toSpreadsheetCsv(csvText)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

// Prefers the server-served export when the live endpoint provides one;
// otherwise builds the CSV client-side. The client builder is kept
// permanently — it's the only thing that works in fixtures mode.
export function downloadRiskCsv(data, presentation, csvUrl) {
    if (csvUrl) {
        const link = document.createElement('a');
        link.href = csvUrl;
        link.click();
        return;
    }
    downloadCsvText(buildRiskCsv(data, presentation), riskCsvFilename(data));
}
